import { Tiny } from "@ant-design/charts";
import {
  Badge,
  Button,
  Card,
  Col,
  Descriptions,
  Empty,
  List,
  Result,
  Row,
  Skeleton,
  Typography,
} from "antd";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { adminApi } from "@/api/client";

type NodeOverviewRow = Record<string, unknown>;

export function MonitoringBoardPage() {
  const { t } = useTranslation();

  const overview = useQuery({
    queryKey: ["admin", "vpn", "nodesOverview_board"],
    queryFn: async () => {
      const { data, response } = await adminApi.GET(
        "/admin/v1/vpn/nodes/overview"
      );
      if (!response.ok) throw new Error("overview");
      return (data ?? {}) as Record<string, unknown>;
    },
  });

  const sparkDemo = Array.from({ length: 14 }).map((_, i) => ({
    day: `${i}`,
    load: Math.max(5, Math.round(60 + Math.sin(i / 2) * 20)),
  }));

  const badges: {
    key: string;
    status: "success" | "warning" | "error" | "processing";
    labelKey: string;
  }[] = [
    { key: "ok", status: "success", labelKey: "monitor.stateOk" },
    { key: "degraded", status: "warning", labelKey: "monitor.stateDegraded" },
    { key: "fault", status: "error", labelKey: "monitor.stateFault" },
    { key: "maintenance", status: "processing", labelKey: "monitor.stateMaint" },
  ];

  const parsedItems: NodeOverviewRow[] = extractItems(overview.data);

  return (
    <div style={{ width: "100%" }}>
      <Typography.Title level={3} style={{ marginTop: 0 }}>
        {t("monitor.title")}
      </Typography.Title>

      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        {badges.map((b) => (
          <Col key={b.key} xs={24} sm={12} xl={6}>
            <Card size="small">
              <Badge status={b.status} text={t(b.labelKey)} />
              <div style={{ height: 72, marginTop: 12 }}>
                <Tiny.Line data={sparkDemo} xField="day" yField="load" />
              </div>
            </Card>
          </Col>
        ))}
      </Row>

      <Card title={t("monitor.fourState")}>
        {overview.isPending ? (
          <Skeleton active paragraph={{ rows: 4 }} />
        ) : overview.error ? (
          <Result
            status="error"
            title={t("monitor.loadErr")}
            extra={
              <Button type="primary" onClick={() => overview.refetch()}>
                {t("common.retry")}
              </Button>
            }
          />
        ) : !parsedItems.length ? (
          <Empty description={t("monitor.noAgent")} />
        ) : (
          <List<NodeOverviewRow>
            bordered
            dataSource={parsedItems}
            locale={{ emptyText: t("monitor.noAgent") }}
            renderItem={(item) => {
              const id = pickId(item);
              const agentMissing = Boolean(item.agent_available === false);
              return (
                <List.Item key={id}>
                  <div style={{ width: "100%" }}>
                    <Descriptions size="small" column={{ xs: 1, md: 3 }}>
                      <Descriptions.Item label={t("monitor.labelNodeId")}>
                        <Typography.Text code>{id}</Typography.Text>
                      </Descriptions.Item>
                      <Descriptions.Item label={t("monitor.labelStatus")}>
                        {String(item.status ?? "unknown")}
                      </Descriptions.Item>
                      <Descriptions.Item label={t("monitor.labelMetrics")}>
                        {agentMissing ? t("monitor.noAgent") : t("monitor.metricsAvailable")}
                      </Descriptions.Item>
                    </Descriptions>
                    <div style={{ height: 64, marginTop: 12 }}>
                      <Tiny.Line data={sparkDemo} xField="day" yField="load" />
                    </div>
                  </div>
                </List.Item>
              );
            }}
          />
        )}
      </Card>
    </div>
  );
}

function extractItems(data: Record<string, unknown> | undefined): NodeOverviewRow[] {
  if (!data) return [];
  const raw =
    data.items ??
    data.nodes ??
    data.rows ??
    data.results;
  return Array.isArray(raw) ? (raw as NodeOverviewRow[]) : [];
}

function pickId(row: NodeOverviewRow): string {
  return String(row.node_id ?? row.id ?? row.nodeId ?? "?");
}
